"use client";

import { useMemo } from "react";
import { QRPreviewProps } from "./types";

import {
  PreviewApps,
  PreviewBusiness,
  PreviewCoupon,
  PreviewEvent,
  PreviewFeedback,
  PreviewImages,
  PreviewLinks,
  PreviewPdf,
  PreviewSocial,
  PreviewUrl,
  PreviewVcard,
  PreviewVideo,
  PreviewWebsite,
  PreviewWifi,
  PreviewYoutube,
  PreviewMp3,
  PreviewText,
  PreviewEmail,
  PreviewUrlStatic,
  PreviewSMS,
  PreviewWhatsapp,
} from "../previews";
import { mergePreviewDataWithTranslations } from "./utils";

import { QRPreviewStyle } from "./QRPreviewStyle";
import { INITIAL_QR_DATA } from "@/contexts/QREditorContext";
import "./styles.scss";

const PREVIEW_COMPONENTS: Record<string, React.ComponentType<any>> = {
  website: PreviewWebsite,
  listoflinks: PreviewLinks,
  pdf: PreviewPdf,
  url: PreviewUrl,
  apps: PreviewApps,
  business: PreviewBusiness,
  image: PreviewImages,
  video: PreviewVideo,
  wifi: PreviewWifi,
  youtube: PreviewYoutube,
  vcard: PreviewVcard,
  social: PreviewSocial,
  event: PreviewEvent,
  coupon: PreviewCoupon,
  feedback: PreviewFeedback,
  mp3: PreviewMp3,
  text: PreviewText,
  email: PreviewEmail,
  "url-static": PreviewUrlStatic,
  sms: PreviewSMS,
  whatsapp: PreviewWhatsapp,
};

const initPreviewDataJson = require("../../data/initial-previews/initPreviewData.json");

export const QRPreviewData = ({
  data,
  previewTranslations,
  isQRInit,
  defaultPreviewTitle,
}: QRPreviewProps) => {
  const finalInitPreviewData = useMemo(() => {
    const initData = initPreviewDataJson.find((qr) => qr.type === data?.type);
    const result = mergePreviewDataWithTranslations(initData, previewTranslations, data.type);
    return result;
  }, [data.type, previewTranslations]);

  const getThemeColor = () => {
    const { themeColor } = data?.data || {};
    const initialTheme = INITIAL_QR_DATA.data.themeColor;

    const hasChanged =
      themeColor?.isCustom ||
      (themeColor &&
        (themeColor.backgroundColor !== initialTheme.backgroundColor ||
          themeColor.buttonColor !== initialTheme.buttonColor));

    return data.currentPageIndex > 0 && hasChanged
      ? themeColor
      : finalInitPreviewData?.data.themeColor;
  };

  if (!data?.type) {
    return (
      <div className="qr-preview__layout__image">
        <QRPreviewStyle
          data={data}
          defaultPreviewTitle={defaultPreviewTitle}
          showEmptyPreview
        />
      </div>
    );
  }

  const PreviewComponent = PREVIEW_COMPONENTS[data.type];
  if (!PreviewComponent) return <div className="qr-preview__layout__image" />;

  const isActualPreview = data?.currentPageIndex > 0 && !isQRInit;
  const componentProps = isActualPreview
    ? data
    : {
        ...finalInitPreviewData,
        data: {
          ...finalInitPreviewData?.data,
          themeColor: getThemeColor(),
        },
        initPreview: true,
      };

  return (
    <div className="qr-preview__layout__image">
      <PreviewComponent
        {...componentProps}
        previewTranslations={previewTranslations}
        preview
        source="editor"
      />
    </div>
  );
};
